import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { CheckCircle, Info, ShieldCheck, TrendingUp, AlertTriangle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import referralService from "../../../services/api/referral.service";
import { useAuth } from "../../../hooks/useAuth";

const KYC_PAGE = "/profile/kyc/basic-info";

const ReferralForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [referralCode, setReferralCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [appliedReferralCode, setAppliedReferralCode] = useState("");
  const [rewardAmount, setRewardAmount] = useState(0);
  const [showKycBanner, setShowKycBanner] = useState(false);
  const [kycRedirectCountdown, setKycRedirectCountdown] = useState(4);
  const countdownRef = useRef(null);

  // Load applied referral code and fetch reward config
  useEffect(() => {
    const savedCode = localStorage.getItem("appliedReferralCode");
    if (savedCode) {
      setAppliedReferralCode(savedCode);
      setReferralCode(savedCode);
      setSuccessMessage("Referral code already applied successfully!");
    } else {
      const pendingCode = localStorage.getItem("pendingReferralCode");
      if (pendingCode) {
        setReferralCode(pendingCode);
        localStorage.removeItem("pendingReferralCode");
        toast.info(
          "A referral code was from your link! Click 'Apply Code' to use it."
        );
      }
    }

    const fetchActiveReward = async () => {
      try {
        const response = await referralService.getActiveConfig();
        if (response.success && response.data) {
          setRewardAmount(response.data.rewardAmount);
        }
      } catch (error) {
        console.error("Error fetching active reward config:", error);
      }
    };

    fetchActiveReward();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    // Check if referral code is already applied
    if (appliedReferralCode) {
      toast.error("Referral code has already been applied!");
      setErrorMessage(
        "You have already applied a referral code. Only one referral code can be used per account."
      );
      return;
    }

    if (!referralCode.trim()) {
      toast.error("Please enter a referral code");
      setErrorMessage("Please enter a referral code");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await referralService.applyReferralCode(
        referralCode,
        "WEB"
      );

      if (response.success) {
        toast.success("Referral code applied successfully!");
        setSuccessMessage("Referral code applied successfully!");
        setAppliedReferralCode(referralCode);
        localStorage.setItem("appliedReferralCode", referralCode);
        localStorage.removeItem("pendingReferralCode");

        // Check KYC status and trigger banner
        const isKycDone = user?.isKycVerified === true;
        setShowKycBanner(true);
        if (!isKycDone) {
          // Start countdown to auto-redirect to KYC page
          let count = 4;
          setKycRedirectCountdown(count);
          countdownRef.current = setInterval(() => {
            count -= 1;
            setKycRedirectCountdown(count);
            if (count <= 0) {
              clearInterval(countdownRef.current);
              navigate(KYC_PAGE);
            }
          }, 1000);
        }
      } else {
        const message =
          response.message || "Something went wrong. Please try again.";
        setErrorMessage(message);
        toast.error(message);
      }
    } catch (error) {
      if (error?.message) {
        setErrorMessage(error.message);
        toast.error(error.message);
      } else {
        setErrorMessage("Failed to apply referral code. Please try again.");
        toast.error("Failed to apply referral code");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearReferralCode = () => {
    // Optional: Add function to clear referral code (for testing purposes)
    localStorage.removeItem("appliedReferralCode");
    setAppliedReferralCode("");
    setReferralCode("");
    setSuccessMessage("");
    setErrorMessage("");
    setShowKycBanner(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    toast.info("Referral code cleared");
  };

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const isKycVerified = user?.isKycVerified === true;

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="mb-2">
        <h2 className="text-xl font-semibold">
          {appliedReferralCode && (
            <span className="text-green-600 font-mono mr-3 bg-green-100 px-2 py-1 rounded text-lg">
              {appliedReferralCode}
            </span>
          )}
          Have a Referral Code?
        </h2>
      </div>
      <p className="text-gray-600 mb-4">
        Enter it below to claim your benefits
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex space-x-2">
          <input
            type="text"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value)}
            placeholder={
              appliedReferralCode
                ? "Referral code applied"
                : "Enter referral code"
            }
            className={`flex-1 min-w-0 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${appliedReferralCode
              ? "border-green-300 bg-green-50 text-green-800 cursor-not-allowed"
              : "border-gray-300"
              }`}
            disabled={isSubmitting || !!appliedReferralCode}
            readOnly={!!appliedReferralCode}
          />

          <button
            type="submit"
            disabled={isSubmitting || !!appliedReferralCode}
            className={`px-4 py-3 font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${appliedReferralCode
              ? "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500"
              : "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
              }`}
          >
            {appliedReferralCode ? (
              <div className="flex items-center justify-center">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Applied
              </div>
            ) : isSubmitting ? (
              <div className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Applying...</span>
              </div>
            ) : (
              "Apply Code"
            )}
          </button>
        </div>

        {errorMessage && (
          <div className="p-3 bg-red-100 text-red-700 rounded-lg" role="alert">
            {errorMessage}
          </div>
        )}

        {successMessage && (
          <div
            className="p-3 bg-green-100 text-green-700 rounded-lg"
            role="alert"
          >
            {successMessage}
          </div>
        )}
      </form>

      {appliedReferralCode && (
        <div className="mt-6 space-y-4">
          {/* Applied code confirmation */}
          <div className="p-4 bg-green-50 border border-green-300 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-green-700 mb-2 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Applied Referral Code:
                </h3>
                <p className="text-green-900 text-lg font-mono bg-white px-3 py-2 rounded border inline-block">
                  {appliedReferralCode}
                </p>
                <p className="text-sm text-green-600 mt-2">
                  Your referral benefits have been activated!
                </p>
              </div>
            </div>
          </div>

          {/* KYC Status Banner — shown after code is applied */}
          {showKycBanner && (
            isKycVerified ? (
              /* ✅ KYC Already Done */
              <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-300 rounded-xl shadow-sm">
                <ShieldCheck className="w-6 h-6 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-bold text-emerald-800 text-base">KYC Verified ✓</h4>
                  <p className="text-sm text-emerald-700 mt-0.5">
                    Great! Your KYC is already complete. You&apos;re all set to earn your referral reward once your referee invests.
                  </p>
                </div>
              </div>
            ) : (
              /* ⚠️ KYC Not Done — countdown redirect */
              <div className="flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-400 rounded-xl shadow-md">
                <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5 shrink-0 animate-bounce" />
                <div className="flex-1">
                  <h4 className="font-bold text-amber-800 text-base">KYC Required to Unlock Reward</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Your referral code has been applied, but you need to complete your KYC verification before the reward can be released.
                  </p>
                  <p className="text-xs text-amber-600 mt-2 font-medium">
                    Redirecting you to KYC in <span className="font-bold text-amber-800">{kycRedirectCountdown}s</span>…
                  </p>
                  <button
                    onClick={() => {
                      if (countdownRef.current) clearInterval(countdownRef.current);
                      navigate(KYC_PAGE);
                    }}
                    className="mt-3 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                  >
                    Complete KYC Now
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* Reward Qualification Instructions - Always Shown */}
      <div className="mt-6 bg-indigo-50 border border-indigo-100 rounded-xl p-5 shadow-sm">
        <h3 className="text-indigo-900 font-bold flex items-center mb-4 text-lg">
          <Info className="w-5 h-5 mr-2 text-indigo-600" />
          How to earn your ₹{rewardAmount || "---"} reward?
        </h3>

        <div className="space-y-4">
          <div className="flex items-start">
            <div className="bg-white p-2 rounded-lg shadow-sm mr-4 border border-indigo-100">
              <ShieldCheck className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h4 className="font-bold text-gray-800">KYC Verified</h4>
              <p className="text-sm text-gray-600">
                Both the Referrer and the Referee (you) must have completed
                their KYC verification.
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="bg-white p-2 rounded-lg shadow-sm mr-4 border border-indigo-100">
              <TrendingUp className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h4 className="font-bold text-gray-800">Investment Required</h4>
              <p className="text-sm text-gray-600">
                Both parties must have at least one <strong>COMPLETED</strong>{" "}
                investment transaction on the platform to release the ₹
                {rewardAmount || "---"} reward.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralForm;
